import React from 'react';
import clsx from 'clsx';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { Button } from '../../../components/UI';
import { InspoJourney, useCapabilities } from '../../../services';
import { useInspoCta } from './InspoCta';

type Step = { title: string; body: string; done?: boolean };
type Plan = { eyebrow: string; title: string; body: string; cta: { label: string; href: string }; alt: { label: string; href: string }; steps: Step[] };

const CONNECT_SHOPIFY = '/settings?tab=channels';

// #region planFor
const planFor = (journey: InspoJourney, hasShopify: boolean, payoutsReady: boolean): Plan => {
    if (journey === 'artist') {
        return {
            eyebrow: 'Your next step',
            title: 'Publish your designs for licensing',
            body: 'Stores license designs from the Wallmates catalog and pay you a royalty on every order they sell. For example, at a 10% royalty a 10 × 8 ft wall earns you about $30. That figure is only an illustration.',
            cta: { label: 'Set up licensing', href: '/product-listing/setup' },
            alt: { label: 'Or open your own store', href: CONNECT_SHOPIFY },
            steps: [
                { title: 'Create a design', body: 'Upload your artwork. Wallmates makes the mockups.' },
                { title: 'Turn on payouts', body: 'Connect Stripe so royalties can reach you.', done: payoutsReady },
                { title: 'Set a royalty and publish', body: 'Your designs join the catalog that stores license from.' }
            ]
        };
    }
    return {
        eyebrow: 'Your next step',
        title: hasShopify ? 'Your store is connected. Now fill it.' : 'Connect your Shopify store',
        body: 'Every brand here sells on its own Shopify store, and Wallmates prints and ships each order. Connecting Shopify also moves you to Store Owner pricing.',
        cta: hasShopify ? { label: 'Create a design', href: '/design-creation' } : { label: 'Connect Shopify', href: CONNECT_SHOPIFY },
        alt: { label: 'Browse designs to license', href: '/product-listing' },
        steps: [
            { title: 'Connect Shopify', body: 'One click, and your products sync.', done: hasShopify },
            { title: 'Add designs', body: 'Create your own, or license designs from artists.' },
            { title: 'Release and sell', body: 'Wallmates prints, packs and ships every order.' }
        ]
    };
};

// #region publicPlan
/** The public version: same story, one button that starts sign-up. */
const publicPlan = (plan: Plan, journey: InspoJourney): Plan => ({
    ...plan,
    title: journey === 'artist' ? 'Turn your art into a wallpaper business' : 'Start your own wallpaper brand',
    cta: { label: 'Get started free', href: '/get-started' }
});

// #region NextStep
/** The one clear action for this viewer: connect Shopify (stores) or publish for licensing (artists). */
export const NextStep: React.FunctionComponent<{ journey: InspoJourney }> = ({ journey }) => {
    const { publicMode, go } = useInspoCta();
    const { hasShopify, connectOnboardingComplete } = useCapabilities();
    // In public nobody is signed in: nothing is done yet, and the one button opens sign-up.
    const plan = publicMode ? publicPlan(planFor(journey, false, false), journey) : planFor(journey, hasShopify, connectOnboardingComplete);
    return (
        <section className="overflow-hidden rounded-2xl bg-blue-600 p-6 text-white sm:p-10">
            <div className="grid gap-6 lg:grid-cols-2 lg:items-center">
                <div className="space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-lavender">{plan.eyebrow}</p>
                    <h2 className="font-display text-3xl font-semibold leading-tight sm:text-4xl">{plan.title}</h2>
                    <p className="text-sm leading-relaxed text-white/90">{plan.body}</p>
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                        <Button color="white" className="dark:[--btn-bg:theme(colors.white)] dark:!text-zinc-950" onClick={() => go(plan.cta.href, journey)}>{plan.cta.label}</Button>
                        {!publicMode && <button type="button" onClick={() => go(plan.alt.href, journey)} className="text-sm font-medium text-white underline underline-offset-4 hover:underline">
                            {plan.alt.label}
                        </button>}
                    </div>
                </div>
                <ol className="space-y-2">
                    {plan.steps.map((s, i) => (
                        <li key={s.title} className={clsx('flex gap-3 rounded-xl p-3', s.done ? 'bg-white/5' : 'bg-white/10')}>
                            {s.done
                                ? <CheckCircleIcon className="h-6 w-6 shrink-0 text-seabreeze" aria-label="Done" />
                                : <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-blue-600">{i + 1}</span>}
                            <div>
                                <p className={clsx('text-sm font-semibold', s.done && 'text-white/70 line-through decoration-white/50')}>{s.title}</p>
                                <p className="text-xs text-white/80">{s.body}</p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
};
